import { AxiosError, AxiosRequestConfig, AxiosResponse, default as axios } from "axios";
import {
    ValidationError,
    ApplicationError,
    SystemError,
    NoResponseError,
    InternalError,
    AccessDeniedError,
    UnauthorizedError
} from "./errors";

import qs from "qs";

const client = axios.create({
    timeout: 5000,
    headers: {
        "Content-Type": "application/json"
    }
});

export interface ApiResponse<T> {
    response?: AxiosResponse<T>;
    error?: Error;
}

// function blobDataReader(data: Blob): Promise<string | ArrayBuffer | null> {
//     const fileReader = new FileReader();

//     return new Promise((resolve, reject) => {
//         fileReader.onerror = () => {
//             fileReader.abort();
//             reject(new Error("Error reading file"));
//         };

//         fileReader.onload = () => {
//             resolve(fileReader.result);
//         };

//         fileReader.readAsText(data);
//     });
// }

async function handleError(
    error: AxiosError<{
        code: string;
        data: any;
        message: string;
    }>
): Promise<Error> {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        switch (error.status) {
            case 400:
                if (error.response?.data) {
                    const responseData = error.response?.data;
                    switch (error.response?.data.code) {
                        case "VALIDATION_ERROR":
                            return new ValidationError(responseData.data);

                        default:
                            return new ApplicationError(
                                responseData.code,
                                responseData.message,
                                responseData.data || null
                            );
                    }
                } else {
                    return new SystemError();
                }
            case 500:
                return new SystemError();
            case 401:
                return new UnauthorizedError();
            case 403:
                return new AccessDeniedError();
            default:
                if (error.response?.data.code) {
                    const responseData = error.response?.data;
                    new ApplicationError(responseData.code, responseData.message, responseData.data || null);
                } else {
                    return new SystemError();
                }
        }
    } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        return new NoResponseError();
    }

    return new InternalError(error.message);
}

async function makeRequest(type: string, url: string, config?: AxiosRequestConfig, data?: any) {
    let response: AxiosResponse;
    let error: Error;
    try {
        switch (type) {
            case "get":
                response = await client.get(url, config);
                break;
            case "post":
                response = await client.post(url, data, config);
                break;
            case "put":
                response = await client.put(url, data, config);
                break;
            case "patch":
                response = await client.patch(url, data, config);
                break;
            case "delete":
                response = await client.delete(url, config);
                break;
            default:
                throw new Error(`Unknown type "${type}"`);
        }
        return {
            response
        };
    } catch (e) {
        if (e instanceof AxiosError) {
            error = await handleError(e);
        } else {
            throw e;
        }
        return {
            error
        };
    }
}

function handleFileDownloadFromResponse(response: AxiosResponse): AxiosResponse {
    let filename = "";
    const disposition = response.headers["content-disposition"];

    if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, "");
    }

    const blob = new Blob([response.data], {
        type: response.headers["content-type"]
    });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    return response;
}

const apiClient = {
    init: function (baseUrl: string): void {
        client.defaults.baseURL = baseUrl;
    },

    url: function (url: string, params: object = {}): string {
        if (params === null) {
            return url;
        }

        for (const [name, value] of Object.entries(params)) {
            url = url.replace(`{${name}}`, String(value));
        }

        return url;
    },

    get: async function (url: string, params: object = {}, config: AxiosRequestConfig = {}) {
        if (params) {
            url = url + "?" + qs.stringify(params);
        }

        return await makeRequest("get", url, config);
    },

    post: async function (
        url: string,
        data: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
        params: object = {}
    ) {
        if (params) {
            url = url + "?" + qs.stringify(params);
        }

        return await makeRequest("post", url, config, data);
    },

    put: async function (
        url: string,
        data: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
        params: object = {}
    ) {
        if (params) {
            url = url + "?" + qs.stringify(params);
        }

        return await makeRequest("put", url, config, data);
    },

    patch: async function (
        url: string,
        data: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
        params: object = {}
    ) {
        if (params) {
            url = url + "?" + qs.stringify(params);
        }

        return await makeRequest("patch", url, config, data);
    },

    delete: async function (url: string, config: AxiosRequestConfig = {}, params: object = {}) {
        if (params) {
            url = url + "?" + qs.stringify(params);
        }

        return await makeRequest("delete", url, config);
    },

    download: async function (url: string, params: object = {}, config: AxiosRequestConfig = {}) {
        config.responseType = "blob";

        if (params) {
            url = url + "?" + qs.stringify(params);
        }

        const response = await makeRequest("get", url, config);

        if (!response.error && response.response) {
            return handleFileDownloadFromResponse(response.response);
        }
    },

    downloadWithPost: async function (url: string, data: { [key: string]: any } = {}, config: AxiosRequestConfig = {}) {
        config.responseType = "blob";

        const response = await makeRequest("post", url, config, data);

        if (!response.error && response.response) {
            return handleFileDownloadFromResponse(response.response);
        }
    },

    upload: async function (
        url: string,
        formData: FormData | null = null,
        config: AxiosRequestConfig = {},
        handleFileDownload = false
    ) {
        const defaultConfig: AxiosRequestConfig = {
            headers: {
                "Content-Type": "multipart/form-data"
            },
            data: {}
        };

        if (handleFileDownload) {
            defaultConfig.responseType = "blob";
        }

        const response = await makeRequest("post", url, { ...defaultConfig, ...config }, formData);

        if (!response.error && response.response) {
            return handleFileDownloadFromResponse(response.response);
        }

        return response;
    },
    setAuthorizationToken: function (token: string) {
        client.defaults.headers.common["Authorization"] = "Bearer " + token;
    },
    deleteAuthorizationToken: function () {
        delete client.defaults.headers.common["Authorization"];
    }
};

export default apiClient;
